import { useState, useMemo } from 'react';
import { StatusBanner } from '../../components/ui';
import { inputClassName, labelClassName } from '../ui/classes';

function AuthField({
  label,
  name,
  type = 'text',
  autoComplete,
  placeholder,
  value,
  onChange,
  required = true,
  minLength,
  maxLength,
  inputMode,
  pattern,
  icon,
  trailing,
}) {
  return (
    <label className="group grid gap-1.5">
      <span className={`${labelClassName} text-[13px] font-semibold text-zinc-700 transition-colors group-focus-within:text-blue-700`}>
        {label}
      </span>
      <div className="relative">
        {icon ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400 transition-colors group-focus-within:text-blue-600">
            {icon}
          </div>
        ) : null}
        <input
          className={`${inputClassName} min-h-11 rounded-xl border-slate-200 bg-white text-sm shadow-[inset_0_1px_2px_rgba(15,23,42,0.04)] transition-[border-color,box-shadow] duration-200 hover:border-slate-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/15 ${icon ? 'pl-10' : ''
            } ${trailing ? 'pr-11' : ''}`}
          name={name}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          inputMode={inputMode}
          pattern={pattern}
        />
        {trailing}
      </div>
    </label>
  );
}

function PasswordStrengthMeter({ password }) {
  const strength = useMemo(() => {
    if (!password) return 0;
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return score;
  }, [password]);

  if (!password) return null;

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = [
    '',
    'bg-red-500 text-red-600',
    'bg-amber-500 text-amber-600',
    'bg-blue-500 text-blue-600',
    'bg-emerald-500 text-emerald-600',
  ];

  return (
    <div className="mt-1 space-y-1.5 animate-[auth-fade-up_0.2s_ease-out_both]">
      <div className="grid grid-cols-4 gap-1.5">
        {[1, 2, 3, 4].map((step) => (
          <div
            key={step}
            className={`h-1.5 rounded-full transition-all duration-300 ${step <= strength ? colors[strength].split(' ')[0] : 'bg-slate-200'
              }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-slate-500">Security strength</span>
        <span className={`font-semibold ${colors[strength].split(' ')[1]}`}>
          {labels[strength]}
        </span>
      </div>
    </div>
  );
}

function AuthPanel({
  form,
  mode,
  status,
  setStatus,
  submitLabel,
  isSubmitting,
  onChange,
  onModeChange,
  onSubmit,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const isSignup = mode === 'signup';

  const passwordToggle = (visible, onToggle) => (
    <button
      type="button"
      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none"
      onClick={onToggle}
      aria-label={visible ? 'Hide password' : 'Show password'}
      tabIndex={-1}
    >
      {visible ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  );

  return (
    <form className="grid w-full gap-5" onSubmit={onSubmit} noValidate>
      <header>
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 border border-blue-200/60">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">
            {isSignup ? 'Get started' : 'Welcome back'}
          </p>
        </div>
        <h2 className="font-display mt-3 text-[1.85rem] font-extrabold tracking-tight text-zinc-900">
          {isSignup ? 'Create your account' : 'Sign in to workspace'}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-zinc-500">
          {isSignup
            ? 'Set up your administrator profile, then connect Amazon SP-API.'
            : 'Enter your credentials to access orders, procurement & inventory.'}
        </p>
      </header>

      {/* Segmented Mode Switcher with Fluid Motion */}
      <div
        className="relative grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 ring-1 ring-slate-200/80"
        role="tablist"
        aria-label="Authentication mode"
      >
        <span
          className={[
            'pointer-events-none absolute inset-y-1 w-[calc(50%-0.25rem)] rounded-lg bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
            mode === 'signup' ? 'translate-x-[calc(100%+0.25rem)]' : 'translate-x-1',
          ].join(' ')}
          aria-hidden="true"
        />
        {[
          { id: 'login', label: 'Sign in' },
          { id: 'signup', label: 'Register' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={mode === tab.id}
            className={[
              'relative z-[1] min-h-10 rounded-lg text-sm font-semibold transition-colors duration-200',
              mode === tab.id ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-800',
            ].join(' ')}
            onClick={() => onModeChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        key={mode}
        className="grid gap-3.5 duration-300 animate-[auth-fade-up_0.35s_cubic-bezier(0.22,1,0.36,1)_both]"
      >
        {isSignup ? (
          <AuthField
            label="Full name"
            name="name"
            autoComplete="name"
            placeholder="John Doe"
            value={form.name}
            onChange={onChange}
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          />
        ) : null}

        <AuthField
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="name@company.com"
          value={form.email}
          onChange={onChange}
          icon={
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
        />

        {isSignup ? (
          <AuthField
            label="Phone number"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder="10-digit mobile number"
            value={form.phone}
            onChange={onChange}
            inputMode="numeric"
            maxLength={10}
            pattern="[0-9]{10}"
            minLength={10}
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            }
          />
        ) : null}

        <div>
          <AuthField
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            placeholder={isSignup ? 'At least 8 characters' : 'Enter your password'}
            value={form.password}
            onChange={onChange}
            minLength="8"
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            }
            trailing={passwordToggle(showPassword, () => setShowPassword((c) => !c))}
          />
          {isSignup ? <PasswordStrengthMeter password={form.password} /> : null}
        </div>

        {isSignup ? (
          <AuthField
            label="Confirm password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Re-enter password"
            value={form.confirmPassword}
            onChange={onChange}
            minLength="8"
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
            trailing={passwordToggle(showConfirmPassword, () =>
              setShowConfirmPassword((c) => !c)
            )}
          />
        ) : null}

        {/* Remember me & Forgot Password on Login */}
        {!isSignup ? (
          <div className="flex items-center justify-between text-xs pt-0.5">
            <label className="flex items-center gap-2 cursor-pointer select-none text-zinc-600 hover:text-zinc-900 transition">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
              />
              <span>Remember this device</span>
            </label>
            <button
              type="button"
              className="font-semibold text-blue-700 hover:text-blue-800 transition"
              onClick={() =>
                setStatus({
                  type: 'info',
                  message: 'To reset your credentials, please contact your workspace administrator.',
                })
              }
            >
              Forgot password?
            </button>
          </div>
        ) : null}
      </div>

      <StatusBanner status={status} setStatus={setStatus} />

      <button
        className="group crm-btn-primary relative min-h-11 w-full overflow-hidden rounded-xl text-[15px] font-bold shadow-[0_16px_36px_-14px_rgba(29,78,216,0.55)] transition duration-200 hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center justify-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            Please wait…
          </span>
        ) : (
          <span className="inline-flex items-center justify-center gap-2">
            <span>{submitLabel}</span>
            <svg
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </span>
        )}
      </button>

      <p className="text-center text-[13px] text-zinc-500">
        {isSignup ? (
          <>
            Already have an account?{' '}
            <button
              type="button"
              className="font-semibold text-blue-700 transition hover:text-blue-800 hover:underline"
              onClick={() => onModeChange('login')}
            >
              Sign in
            </button>
          </>
        ) : (
          <>
            Don't have an account yet?{' '}
            <button
              type="button"
              className="font-semibold text-blue-700 transition hover:text-blue-800 hover:underline"
              onClick={() => onModeChange('signup')}
            >
              Create an account
            </button>
          </>
        )}
      </p>
    </form>
  );
}

export default AuthPanel;

